/**
 * Phase 17 Plan 17-02 — TanStack Query v5 hooks for KPI Template management.
 *
 * Query key: ['kpi-templates'] for the list.
 * All mutations invalidate ['kpi-templates'] on success.
 *
 * Requires `kpis.manage_templates` permission (Admin-only — enforced by
 * KpiTemplatesListPage mount-time gate + backend RBAC).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/lib/api/error';
import {
  createKpiTemplateRequest,
  deleteKpiTemplateRequest,
  listKpiTemplatesRequest,
  updateKpiTemplateRequest,
} from '@/services/kpi-templates';
import type {
  CreateKpiTemplateBody,
  KpiTemplate,
  UpdateKpiTemplateBody,
} from '@/types/kpi-template';

// ---------------------------------------------------------------------------
// Query hooks
// ---------------------------------------------------------------------------

/** List all KPI templates (admin-only). */
export function useKpiTemplatesQuery() {
  return useQuery<KpiTemplate[], ApiError>({
    queryKey: ['kpi-templates'],
    queryFn: () => listKpiTemplatesRequest(),
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------
// Mutation hooks
// ---------------------------------------------------------------------------

/** Create a new KPI template (409 on duplicate name). */
export function useCreateKpiTemplateMutation() {
  const qc = useQueryClient();
  return useMutation<KpiTemplate, ApiError, CreateKpiTemplateBody>({
    mutationFn: (body) => createKpiTemplateRequest(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['kpi-templates'] });
    },
  });
}

interface UpdateKpiTemplateVars {
  readonly id: number;
  readonly body: UpdateKpiTemplateBody;
}

/** Update an existing KPI template. */
export function useUpdateKpiTemplateMutation() {
  const qc = useQueryClient();
  return useMutation<KpiTemplate, ApiError, UpdateKpiTemplateVars>({
    mutationFn: ({ id, body }) => updateKpiTemplateRequest(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['kpi-templates'] });
    },
  });
}

/** Delete a KPI template (204 No Content). */
export function useDeleteKpiTemplateMutation() {
  const qc = useQueryClient();
  return useMutation<undefined, ApiError, number>({
    mutationFn: (id) => deleteKpiTemplateRequest(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['kpi-templates'] });
    },
  });
}
